import type { FilterValue } from "~/types/pagination";

/**
 * Builds a pagination URL with preserved filters
 *
 * @param baseUrl - Base category URL with trailing slash (e.g., /en-it/c/makeup/)
 * @param page - Page number (1-indexed, page 1 omits parameter)
 * @param filters - Optional filters to preserve in query string (supports multi-value)
 * @returns Complete URL with query parameters
 */
export function buildPaginationUrl(baseUrl: string, page: number, filters?: Record<string, FilterValue>): string {
  const params = new URLSearchParams();

  // Add filters with multi-value support
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v));
      } else {
        params.set(key, value);
      }
    });
  }

  // Only add page parameter if > 1 (page 1 is the clean URL)
  if (page > 1) {
    params.set("page", page.toString());
  }

  const query = params.toString();
  return query ? `${baseUrl}?${query}` : baseUrl;
}

/**
 * Extracts the page number from URL query
 *
 * @param pageParam - Page parameter from router query
 * @returns Page number (default: 1 if not present or invalid)
 */
export function getPageFromQuery(pageParam: string | string[] | undefined): number {
  if (!pageParam) return 1;

  const page = parseInt(Array.isArray(pageParam) ? pageParam[0] : pageParam, 10);
  return isNaN(page) || page < 1 ? 1 : page;
}

/**
 * Calculates total number of pages based on product count
 *
 * @param totalProducts - Total number of products
 * @param pageSize - Products per page
 * @returns Total pages (minimum 1)
 */
export function calculateTotalPages(totalProducts: number, pageSize: number): number {
  if (totalProducts <= 0 || pageSize <= 0) return 1;
  return Math.ceil(totalProducts / pageSize);
}
